/**
 * Trading Schedule Definitions & Utilities
 *
 * Centralizes market-hours logic for time-restricted asset classes
 * (US equities, commodities, etc.). All schedule checks are performed
 * in US Eastern Time and handle EDT/EST transitions automatically
 * via the Intl API — no external timezone libraries required.
 */

// ── Schedule identifiers ────────────────────────────────────────────
export const TRADING_SCHEDULES = {
  /** Mon–Fri 9:30 AM – 4:00 PM ET */
  US_STOCKS: 'us_stocks',
  /** Sun 6 PM – Fri 5 PM ET, daily 1-hr break 5–6 PM ET */
  US_COMMODITIES: 'us_commodities',
};

/** Human-readable labels for UI display */
export const SCHEDULE_LABELS = {
  [TRADING_SCHEDULES.US_STOCKS]: 'Mon–Fri 9:30 AM – 4:00 PM ET',
  [TRADING_SCHEDULES.US_COMMODITIES]: 'Sun 6 PM – Fri 5 PM ET (1 hr daily break)',
};

// ── Internal helpers ────────────────────────────────────────────────

/** Convert hours + minutes (+ optional seconds) to total seconds from midnight. */
const toSec = (h, m, s = 0) => h * 3600 + m * 60 + s;

const DAY_MAP = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const SECONDS_PER_DAY = 86400;

/**
 * Get the current date-time components in US Eastern Time.
 * Works in all browsers / Node >= 13 that support Intl.DateTimeFormat.
 * @returns {{ dayOfWeek: number, hour: number, minute: number, second: number }}
 */
export const getNowInET = () => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const get = (type) => parts.find((p) => p.type === type)?.value;

  return {
    dayOfWeek: DAY_MAP[get('weekday')],
    hour: parseInt(get('hour'), 10),
    minute: parseInt(get('minute'), 10),
    second: parseInt(get('second'), 10),
  };
};

// ── Per-schedule open checks ────────────────────────────────────────

/**
 * US equities: Mon–Fri 9:30 AM – 4:00 PM ET.
 * @param {{ dayOfWeek: number, hour: number, minute: number }} et
 */
const isUSStocksOpen = ({ dayOfWeek, hour, minute }) => {
  if (dayOfWeek === 0 || dayOfWeek === 6) return false; // Weekend
  const t = toSec(hour, minute);
  return t >= toSec(9, 30) && t < toSec(16, 0);
};

/**
 * US commodities (CME Globex style):
 *   Opens  — Sunday  18:00 ET
 *   Closes — Friday  17:00 ET
 *   Daily break — 17:00–18:00 ET (Mon–Thu)
 * @param {{ dayOfWeek: number, hour: number }} et
 */
const isUSCommoditiesOpen = ({ dayOfWeek, hour }) => {
  if (dayOfWeek === 6) return false; // Saturday — fully closed
  if (dayOfWeek === 0) return hour >= 18; // Sunday — opens at 18:00
  if (dayOfWeek === 5) return hour < 17; // Friday — closes at 17:00
  // Mon–Thu: open 24 hrs except daily maintenance break at 17:00–18:00
  return hour !== 17;
};

// ── Public API ──────────────────────────────────────────────────────

/**
 * Check whether a scheduled market is currently open.
 * Markets with no schedule (or an unknown schedule) are treated as always-open.
 * @param {string|null|undefined} scheduleType  One of TRADING_SCHEDULES values
 * @returns {boolean}
 */
export const isScheduledMarketOpen = (scheduleType) => {
  if (!scheduleType) return true;
  const et = getNowInET();
  switch (scheduleType) {
    case TRADING_SCHEDULES.US_STOCKS:
      return isUSStocksOpen(et);
    case TRADING_SCHEDULES.US_COMMODITIES:
      return isUSCommoditiesOpen(et);
    default:
      return true;
  }
};

/**
 * Get the number of whole seconds until the next market open.
 * Returns 0 when the market is already open.
 * @param {string|null|undefined} scheduleType
 * @returns {number}
 */
export const getSecondsUntilOpen = (scheduleType) => {
  if (!scheduleType) return 0;

  const et = getNowInET();
  const now = toSec(et.hour, et.minute, et.second);

  // ── US Stocks ─────────────────────────────────────────────────
  if (scheduleType === TRADING_SCHEDULES.US_STOCKS) {
    if (isUSStocksOpen(et)) return 0;

    const openAt = toSec(9, 30);

    // Weekday, before open → opens later today
    if (et.dayOfWeek >= 1 && et.dayOfWeek <= 5 && now < openAt) {
      return openAt - now;
    }

    // After close (or weekend) → figure out how many days to next weekday 9:30
    let daysToWait;
    switch (et.dayOfWeek) {
      case 5: daysToWait = 3; break; // Fri after close → Mon
      case 6: daysToWait = 2; break; // Sat → Mon
      case 0: daysToWait = 1; break; // Sun → Mon
      default: daysToWait = 1;       // Mon–Thu after close → next day
    }

    return (SECONDS_PER_DAY - now) + (daysToWait - 1) * SECONDS_PER_DAY + openAt;
  }

  // ── US Commodities ────────────────────────────────────────────
  if (scheduleType === TRADING_SCHEDULES.US_COMMODITIES) {
    if (isUSCommoditiesOpen(et)) return 0;

    const reopenAt = toSec(18, 0);

    // Saturday → Sunday 18:00
    if (et.dayOfWeek === 6) {
      return (SECONDS_PER_DAY - now) + reopenAt;
    }

    // Sunday before 18:00
    if (et.dayOfWeek === 0 && now < reopenAt) {
      return reopenAt - now;
    }

    // Friday ≥ 17:00 → Sunday 18:00 (rest of Fri + all of Sat + Sun to 18:00)
    if (et.dayOfWeek === 5 && et.hour >= 17) {
      return (SECONDS_PER_DAY - now) + SECONDS_PER_DAY + reopenAt;
    }

    // Mon–Thu during daily break (17:00–18:00) → reopens same day at 18:00
    if (et.hour === 17) {
      return reopenAt - now;
    }

    return 0; // Defensive fallback
  }

  return 0;
};
